import clsx from 'clsx';
import Button, {ButtonProps} from 'components/Button';
import LinkButton, {LinkButtonProps} from 'components/LinkButton';
import TooltipWrapper from 'components/Tooltip';
import {H1, H2, H3, H4, HeaderProps} from 'components/Typography';
import {AriaRole, FC, forwardRef, ReactNode} from 'react';
import {hasMarginClass} from 'utils/functions';

type Level = 1 | 2 | 3 | 4;

const LEVELS: Record<Level, FC<HeaderProps>> = {
    1: H1,
    2: H2,
    3: H3,
    4: H4,
};

export type SectionHeaderBase = {
    // children prop is for Tooltip
    children?: ReactNode;
    className?: string;
    classNameSubtitle?: string;
    classNameTitle?: string;
    hideHR?: boolean;
    level?: Level;
    role?: AriaRole;
    subtitle?: ReactNode;
    subtitleTop?: boolean;
    title: ReactNode;
};

export type SectionHeaderButtonProps = {label: string} & Omit<
    ButtonProps,
    'children' | 'size'
>;

export type SectionHeaderProps = SectionHeaderBase &
    (SectionHeaderWithButtonProps | SectionHeaderWithLinkButtonProps);

type SectionHeaderWithButtonProps = {
    button?: SectionHeaderButtonProps;
    link?: never;
    tooltipMessage?: string;
};

type SectionHeaderWithLinkButtonProps = {
    button?: never;
    link?: {label: string} & Omit<LinkButtonProps, 'children' | 'size'>;
    tooltipMessage?: string;
};

const SectionHeader = forwardRef<HTMLDivElement, SectionHeaderProps>(
    (
        {
            button,
            children,
            className,
            classNameSubtitle,
            classNameTitle,
            hideHR,
            level = 1,
            link,
            role,
            subtitle,
            subtitleTop = false,
            title,
            tooltipMessage,
        },
        ref,
    ) => {
        const Header = LEVELS[level];
        const titleElement =
            button || link ? (
                <div className="flex items-end">
                    <Header
                        className={clsx(
                            'flex-1',
                            classNameTitle,
                            hasMarginClass(className) && 'mt-0',
                            'mx-0 mb-0',
                        )}
                    >
                        {title}
                        {children}
                    </Header>
                    <TooltipWrapper message={tooltipMessage}>
                        {button && (
                            <Button {...button} size="sm">
                                {button.label}
                            </Button>
                        )}
                        {link && (
                            <LinkButton {...link} size="sm">
                                {link?.label}
                            </LinkButton>
                        )}
                    </TooltipWrapper>
                </div>
            ) : (
                <Header
                    className={clsx(
                        classNameTitle,
                        hasMarginClass(className) && 'mt-0',
                        'mx-0 mb-0',
                    )}
                >
                    {title}
                    {children}
                </Header>
            );

        return (
            <div className={clsx(className, 'mt-0')} ref={ref} role={role}>
                {subtitleTop && subtitle && (
                    <div className={clsx('subheading', classNameSubtitle)}>
                        {subtitle}
                    </div>
                )}
                {titleElement}
                {!subtitleTop && subtitle && (
                    <div className={clsx('subheading', classNameSubtitle)}>
                        {subtitle}
                    </div>
                )}
                {!hideHR && <hr className="mt-2" />}
            </div>
        );
    },
);

SectionHeader.displayName = 'SectionHeader';

export default SectionHeader;
